// Etichette italiane dei codici che l'API pubblica in inglese.
//
// Stanno tutte qui perché le stesse parole compaiono nell'agenda, nella scheda della consegna
// e nella coda del back-office: se cambiano, devono cambiare in un punto solo. Il tono del
// segnalatore (`BadgeTone`) viaggia insieme all'etichetta, così lo stesso stato ha sempre lo
// stesso colore in tutte le schermate.

import type { BadgeTone } from '../../ui/primitives'
import type {
  Delivery,
  DeliveryOutcome,
  DeliveryStatus,
  ReceiptDocumentStatus,
  TimeSlot,
} from '../../api/types'

// --- stato della consegna ----------------------------------------------------------------

const STATUS_LABELS: Record<DeliveryStatus, string> = {
  ordered: 'Ordinata',
  shipmentNotified: 'Spedizione notificata',
  documentReceived: 'Documento ricevuto',
  completed: 'Completata',
}

const STATUS_TONES: Record<DeliveryStatus, BadgeTone> = {
  ordered: 'muted',
  shipmentNotified: 'info',
  documentReceived: 'warn',
  completed: 'ok',
}

export function statusLabel(status: DeliveryStatus): string {
  return STATUS_LABELS[status] ?? status
}

export function statusTone(status: DeliveryStatus): BadgeTone {
  return STATUS_TONES[status] ?? 'neutral'
}

/** Gli stati in ordine di avanzamento: serve alla legenda dell'agenda. */
export const STATUS_ORDER: DeliveryStatus[] = ['ordered', 'shipmentNotified', 'documentReceived', 'completed']

// --- documento di carico -----------------------------------------------------------------

const DOCUMENT_LABELS: Record<ReceiptDocumentStatus, string> = {
  missing: 'Documento mancante',
  attached: 'Documento allegato',
  rejected: 'Documento respinto',
  cedi: 'Documento CEDI',
  shipmentAdvice: 'Avviso di spedizione',
}

const DOCUMENT_TONES: Record<ReceiptDocumentStatus, BadgeTone> = {
  missing: 'warn',
  attached: 'info',
  rejected: 'danger',
  cedi: 'muted',
  shipmentAdvice: 'muted',
}

export function documentLabel(status: ReceiptDocumentStatus): string {
  return DOCUMENT_LABELS[status] ?? status
}

export function documentTone(status: ReceiptDocumentStatus): BadgeTone {
  return DOCUMENT_TONES[status] ?? 'neutral'
}

/**
 * Nell'agenda si segnalano solo i tre casi su cui l'utente può agire: manca, è allegato, è
 * stato respinto. Il documento CEDI e l'avviso di spedizione sono la normalità e non meritano
 * un segnale, altrimenti ogni consegna avrebbe un'etichetta e nessuna risalterebbe.
 */
export function documentFlag(status: ReceiptDocumentStatus): { label: string; tone: BadgeTone } | undefined {
  if (status !== 'missing' && status !== 'attached' && status !== 'rejected') return undefined
  return { label: documentLabel(status), tone: documentTone(status) }
}

// --- fascia oraria -----------------------------------------------------------------------

/** L'API può rispondere sia in minuscolo sia con l'iniziale maiuscola: normalizziamo qui. */
function normalizeSlot(slot: TimeSlot): 'none' | 'morning' | 'afternoon' {
  const value = String(slot).toLowerCase()
  if (value === 'morning') return 'morning'
  if (value === 'afternoon') return 'afternoon'
  return 'none'
}

/** Forma breve per la pastiglia in agenda. Stringa vuota quando la fascia non è indicata. */
export function slotLabel(slot: TimeSlot): string {
  const value = normalizeSlot(slot)
  return value === 'morning' ? 'Mattina' : value === 'afternoon' ? 'Pomeriggio' : ''
}

/** Forma estesa per la scheda della consegna, con l'orario che la fascia sottintende. */
export function slotDetail(slot: TimeSlot): string {
  const value = normalizeSlot(slot)
  if (value === 'morning') return 'Mattina (entro le 14)'
  if (value === 'afternoon') return 'Pomeriggio (dalle 14)'
  return 'Nessuna fascia concordata'
}

// --- esito della consegna ----------------------------------------------------------------

const OUTCOME_LABELS: Record<DeliveryOutcome, string> = {
  onTime: 'Puntuale',
  late: 'In ritardo',
  partial: 'Parziale',
  notDelivered: 'Non consegnata',
  early: 'In anticipo',
}

const OUTCOME_TONES: Record<DeliveryOutcome, BadgeTone> = {
  onTime: 'ok',
  late: 'warn',
  partial: 'warn',
  notDelivered: 'danger',
  early: 'info',
}

export function outcomeLabel(outcome: DeliveryOutcome): string {
  return OUTCOME_LABELS[outcome] ?? outcome
}

export function outcomeTone(outcome: DeliveryOutcome): BadgeTone {
  return OUTCOME_TONES[outcome] ?? 'neutral'
}

/** Gli esiti nell'ordine in cui vengono proposti nella tendina. */
export const OUTCOME_OPTIONS: DeliveryOutcome[] = ['onTime', 'early', 'late', 'partial', 'notDelivered']

// --- utilità comuni ----------------------------------------------------------------------

export function originLabel(delivery: Delivery): string {
  return delivery.origin === 'cedi' ? 'Rifornimento dal CEDI' : 'Fornitore diretto'
}

/** Intestazione della consegna: il nome del fornitore, o "CEDI" quando il nome manca. */
export function deliveryTitle(delivery: Delivery): string {
  if (delivery.supplierName) return delivery.supplierName
  return delivery.origin === 'cedi' ? 'CEDI' : delivery.supplierCode
}

/** Filtro fornitore digitato a mano: confronta sia il nome sia il codice. */
export function matchesSupplier(delivery: Delivery, text: string): boolean {
  const query = text.trim().toLowerCase()
  if (!query) return true
  return (
    delivery.supplierName.toLowerCase().includes(query) ||
    delivery.supplierCode.toLowerCase().includes(query)
  )
}

/** Dimensione di un allegato in forma leggibile. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} byte`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`
  return `${(bytes / (1024 * 1024)).toLocaleString('it-IT', { maximumFractionDigits: 1 })} MB`
}
